import React, { useEffect, useState } from "react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PREVIEW_HOST_SUFFIX = ".preview.emergentagent.com";
const UNLOCKED_FLAG = "verifaeye_preview_unlocked_v1";

const isPreviewHost = () =>
  typeof window !== "undefined" &&
  window.location.hostname.toLowerCase().endsWith(PREVIEW_HOST_SUFFIX);

export default function PreviewGate({ children }) {
  const [status, setStatus] = useState(() =>
    !isPreviewHost() || sessionStorage.getItem(UNLOCKED_FLAG) === "1" ? "unlocked" : "checking"
  );
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (status !== "checking") return;
    // Ping any authenticated backend endpoint. 401 => locked, 200 => cookie already valid.
    fetch(`${API_URL}/api/health`, { credentials: "include" })
      .then((r) => {
        if (r.status === 401) {
          setStatus("locked");
        } else {
          sessionStorage.setItem(UNLOCKED_FLAG, "1");
          setStatus("unlocked");
        }
      })
      .catch(() => setStatus("locked"));
  }, [status]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/_preview-gate/unlock`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      if (res.ok) {
        sessionStorage.setItem(UNLOCKED_FLAG, "1");
        setStatus("unlocked");
      } else if (res.status === 401) {
        setError("Invalid credentials");
      } else {
        setError("Unable to unlock preview");
      }
    } catch (err) {
      setError("Network error");
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "unlocked") return children;

  if (status === "checking") {
    return (
      <div
        data-testid="preview-gate-loading"
        style={{
          minHeight: "100vh",
          background: "#0b0f19",
          color: "#e5e7eb",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
        }}
      >
        <span>Loading preview…</span>
      </div>
    );
  }

  return (
    <div
      data-testid="preview-gate"
      style={{
        minHeight: "100vh",
        background: "linear-gradient(180deg,#0b0f19 0%,#111827 100%)",
        color: "#e5e7eb",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1.5rem",
        fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
      }}
    >
      <form
        onSubmit={submit}
        style={{
          width: "100%",
          maxWidth: 380,
          background: "rgba(17,24,39,0.85)",
          border: "1px solid rgba(148,163,184,0.2)",
          borderRadius: 12,
          padding: "2rem",
          boxShadow: "0 20px 40px rgba(0,0,0,0.4)",
        }}
      >
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, letterSpacing: 2, color: "#94a3b8", textTransform: "uppercase" }}>
            Verifaeye · Preview
          </div>
          <h1 style={{ margin: "8px 0 4px", fontSize: 22, fontWeight: 600 }}>Restricted access</h1>
          <p style={{ margin: 0, color: "#94a3b8", fontSize: 14 }}>
            This preview environment is private. Sign in to continue.
          </p>
        </div>
        <label style={{ display: "block", fontSize: 13, color: "#cbd5e1", marginBottom: 6 }}>
          Username
        </label>
        <input
          data-testid="preview-gate-username"
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="username"
          required
          style={{
            width: "100%",
            padding: "10px 12px",
            marginBottom: 14,
            background: "#0f172a",
            border: "1px solid #334155",
            borderRadius: 8,
            color: "#e5e7eb",
            fontSize: 14,
          }}
        />
        <label style={{ display: "block", fontSize: 13, color: "#cbd5e1", marginBottom: 6 }}>
          Password
        </label>
        <input
          data-testid="preview-gate-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
          style={{
            width: "100%",
            padding: "10px 12px",
            marginBottom: 14,
            background: "#0f172a",
            border: "1px solid #334155",
            borderRadius: 8,
            color: "#e5e7eb",
            fontSize: 14,
          }}
        />
        {error && (
          <div
            data-testid="preview-gate-error"
            style={{ color: "#f87171", fontSize: 13, marginBottom: 12 }}
          >
            {error}
          </div>
        )}
        <button
          data-testid="preview-gate-submit"
          type="submit"
          disabled={submitting}
          style={{
            width: "100%",
            padding: "10px 14px",
            background: submitting ? "#334155" : "#0ea5e9",
            color: "#0b0f19",
            border: 0,
            borderRadius: 8,
            fontWeight: 600,
            fontSize: 14,
            cursor: submitting ? "not-allowed" : "pointer",
          }}
        >
          {submitting ? "Unlocking…" : "Unlock preview"}
        </button>
        <p style={{ marginTop: 16, fontSize: 12, color: "#64748b", textAlign: "center" }}>
          Production site: <span style={{ color: "#cbd5e1" }}>verifaeye.com.au</span>
        </p>
      </form>
    </div>
  );
}
